/**
 * Аналитика десктопного клиента (PostHog).
 *
 * Единственная точка отправки продуктовых событий из интерфейса. Каталог
 * событий типизирован: имя события связано с формой его свойств, поэтому
 * «событие с чужими полями» не собирается. Без `VITE_POSTHOG_KEY` модуль
 * превращается в no-op — локальная разработка и CI не требуют учётной
 * записи PostHog, ровно как и серверная аналитика (`posthogService`).
 *
 * Отправка — побочный эффект: ни один вызов не бросает, иначе сбой
 * аналитики ломал бы пользовательский сценарий.
 */

import posthog from 'posthog-js';

import type { CurriculumLevel, Strategy } from '@aptile/shared';

import { logger } from '../lib/logger';

/**
 * Свойства событий. Объявлены `type`, а не `interface`: типы-литералы
 * получают неявную индексную сигнатуру, поэтому совместимы с
 * `Record<string, ...>` из сигнатуры `posthog.capture`.
 */
export type DiagnosticCompleteProperties = {
  readonly memoryScore: number;
  readonly soundSymbolScore: number;
  readonly grammarScore: number;
  readonly strategy: Strategy;
};

export type LessonStartProperties = {
  readonly lessonId: string;
  readonly moduleId: string;
  readonly strategy: Strategy;
};

export type LessonCompleteProperties = {
  readonly lessonId: string;
  readonly strategy: Strategy;
  readonly timeSpentMs: number;
  readonly firstTryCorrect: boolean;
};

export type ChatSessionCompleteProperties = {
  readonly topic: string;
  /** Сколько слов вокабуляра было в обороте: считаем число, а не сами слова. */
  readonly vocabularyUsed: number;
};

export type VocabularyAddedProperties = {
  readonly word: string;
};

export type OnboardingCompleteProperties = {
  /** zero — «с нуля», placement — прошёл тест, skipped — открыл тест и передумал. */
  readonly path: 'zero' | 'placement' | 'skipped';
  readonly level: CurriculumLevel;
};

export type SessionEndProperties = {
  readonly duration: number;
  readonly lessonsDone: number;
};

/** Микрофон не дали: точная причина (имя ошибки браузера) и окружение. */
export type MicErrorProperties = {
  readonly stage: string;
  readonly kind: string;
  readonly name: string;
  readonly message: string;
  readonly secureContext: boolean;
  readonly protocol: string;
  readonly platform: string;
  readonly standalone: boolean;
  readonly inApp: string;
  readonly speechRecognition: boolean;
  readonly mediaRecorder: boolean;
  readonly audioSession: string;
};

export type MicDroppedProperties = {
  readonly reason: string;
  readonly heldSec: number;
  readonly platform: string;
  readonly standalone: boolean;
  readonly audioSession: string;
  /** Точная ошибка браузера перед обрывом (NotAllowedError, NotReadableError, InvalidStateError…). */
  readonly errorName: string;
  /** Состояние аудиоконтекста: running, suspended, interrupted. */
  readonly contextState: string;
};

export type MicRecoveredProperties = {
  readonly stage: string;
  readonly attempts: number;
  readonly platform: string;
};

/**
 * Каталог событий: имя → его свойства. Новое событие без записи здесь
 * не попадёт в приложение — тип `track` его не примет.
 */
export type AnalyticsEvents = {
  diagnostic_complete: DiagnosticCompleteProperties;
  lesson_start: LessonStartProperties;
  lesson_complete: LessonCompleteProperties;
  chat_session_complete: ChatSessionCompleteProperties;
  vocabulary_added: VocabularyAddedProperties;
  session_end: SessionEndProperties;
  onboarding_complete: OnboardingCompleteProperties;
  mic_error: MicErrorProperties;
  mic_recovered: MicRecoveredProperties;
  mic_dropped: MicDroppedProperties;
};

export type AnalyticsEventName = keyof AnalyticsEvents;
export type AnalyticsProperties<E extends AnalyticsEventName> = AnalyticsEvents[E];

/** Ключ проекта: `unknown`, потому что `import.meta.env` не типизирован. */
const apiKey: unknown = import.meta.env.VITE_POSTHOG_KEY;
const apiHost: unknown = import.meta.env.VITE_POSTHOG_HOST;

/** Хост по умолчанию — тот же, что у серверной аналитики. */
function resolveHost(): string {
  return typeof apiHost === 'string' && apiHost.length > 0
    ? apiHost
    : 'https://eu.i.posthog.com';
}

let initialized = false;

/**
 * Включает аналитику один раз за запуск окна. Без ключа ничего не делает.
 * Автозахват выключен: у нас hash-роутинг и SPA, переходы размечаем сами.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (typeof apiKey !== 'string' || apiKey.length === 0) return;

  posthog.init(apiKey, {
    api_host: resolveHost(),
    capture_pageview: false,
    autocapture: false,
    // События переживают перезапуск окна: хранилище — localStorage.
    persistence: 'localStorage',
  });
  initialized = true;
}

/** Привязывает события к пользователю приложения. */
export function identifyUser(userId: string): void {
  if (!initialized) return;

  try {
    posthog.identify(userId);
  } catch (error) {
    logger.warn('[analytics] не удалось связать пользователя', error);
  }
}

/** Разрывает связь с пользователем (выход, сброс профиля). */
export function resetAnalytics(): void {
  if (!initialized) return;

  try {
    posthog.reset();
  } catch (error) {
    logger.warn('[analytics] не удалось сбросить пользователя', error);
  }
}

/**
 * Отправляет событие каталога. Имя и свойства связаны типом, поэтому
 * опечатка в имени или лишнее поле — ошибка компиляции, а не молчаливый
 * мусор в аналитике.
 */
export function track<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsProperties<E>,
): void {
  if (!initialized) return;

  try {
    posthog.capture(event, properties);
  } catch (error) {
    logger.warn(`[analytics] не удалось отправить событие «${event}»`, error);
  }
}
